import React from "react";
import { motion } from "framer-motion";
import { Scale, Building2, Award, ShieldCheck, BadgeCheck, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";

const items = [
  {
    icon: Scale,
    title: "Justiça Federal",
    desc: "Credenciamento oficial para atos de fé pública em traduções juramentadas.",
  },
  {
    icon: Building2,
    title: "JUCEA-AM",
    desc: "Registro junto à Junta Comercial do Estado do Amazonas — Matrícula Nº 45.",
  },
  {
    icon: Award,
    title: "Certidão de Excelência",
    desc: "Reconhecimento por atuação destacada em traduções e interpretações oficiais.",
  },
  {
    icon: BadgeCheck,
    title: "GOV.BR Ouro",
    desc: "Assinatura digital de nível Ouro — máxima validade jurídica no Brasil.",
  },
];

const Credentials = () => {
  return (
    <section id="credenciamentos" data-testid="credentials-section" className="py-24 md:py-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="overline mb-5"><span className="gold-rule mr-3 align-middle" />Credenciamentos</p>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
              Autoridade reconhecida.
              <br /> <span className="italic">Conformidade nacional.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#475569] text-base lg:text-lg leading-relaxed">
              Cada documento traduzido pela {SITE.brandName} é realizado por tradutor
              público juramentado e possui{" "}
              <span className="text-[#0A1128] font-medium">fé pública</span>, sendo aceito
              por órgãos públicos, consulados, cartórios, universidades e demais
              instituições em todo o território nacional, bem como por instituições
              públicas e privadas internacionais que mantenham convênios, acordos ou
              reconheçam a validade das traduções juramentadas emitidas no Brasil.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`credential-card-${i}`}
                className="group bg-white p-8 md:p-10 hover:bg-[#0A1128] transition-colors duration-500 cursor-default"
              >
                <Icon
                  size={32}
                  strokeWidth={1.25}
                  className="text-[#C5A059] mb-6"
                />
                <h3 className="font-serif-display text-xl md:text-2xl text-[#0A1128] group-hover:text-white transition-colors mb-3">
                  {it.title}
                </h3>
                <p className="text-sm text-[#475569] group-hover:text-white/70 transition-colors leading-relaxed">
                  {it.desc}
                </p>
                <div className="mt-6 h-px w-8 bg-[#C5A059]" />
              </motion.div>
            );
          })}
        </div>

        {/* Seal showcase */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          className="mt-20 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16"
        >
          <img
            src={SITE.media.seal}
            alt="Selo oficial — JUCEA-AM"
            data-testid="credential-seal"
            className="w-32 md:w-40 opacity-80"
          />
          <div className="text-center md:text-left max-w-md">
            <p className="overline mb-2">Selo Oficial</p>
            <p className="font-serif-display text-2xl md:text-3xl text-[#0A1128] leading-tight">
              "Tradutor Público e Intérprete Juramentado"
            </p>
            <p className="text-sm text-[#475569] mt-2">
              {SITE.professionalName} — Matrícula Nº 45 · JUCEA-AM
            </p>
            <a
              href={SITE.juceaVerifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="jucea-verify-link"
              className="mt-5 inline-flex items-center gap-2 text-sm text-[#0A1128] border-b border-[#C5A059] pb-1 hover:text-[#C5A059] transition-colors"
            >
              <ShieldCheck size={16} strokeWidth={1.5} className="text-[#C5A059]" />
              <span>Verificar credenciamento oficial na JUCEA-AM</span>
              <ExternalLink size={14} strokeWidth={1.5} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Credentials;
